<template>
  <SlidingPanel
    :isPanelVisible="isPanelOpen"
    :onClose="onClose"
    :on-confirm="onConfirm"
    :close-title="panelTitle"
    :display-confirm-button="true"
    :button-disabled="showErrors || isLoading"
    :is-loading="isLoading"
    has-overlay
  >
    <section class="d-flex flex-column gap-3 justify-content-between">
      <div>
        <Input
          id="first-name"
          :label="t('travellers.firstName')"
          :placeholder="t('travellers.firstName')"
          :value="updatedTravellerData.firstName || ''"
          @input="value => updateFields('firstName', value)"
          :error="t('errorMessages.fieldMandatory')"
          :show-error="invalidFields.firstName"
          :readOnly="isLoading"
        />
        <Input
          id="last-name"
          :label="t('travellers.lastName')"
          :placeholder="t('travellers.lastName')"
          :value="updatedTravellerData.lastName || ''"
          @input="value => updateFields('lastName', value)"
          :show-error="invalidFields.lastName"
          :error="t('errorMessages.fieldMandatory')"
          :readOnly="isLoading"
        />

        <DatePicker
          :date="updatedTravellerData.birthday || ''"
          @date-change="value => updateFields('birthday', value)"
          :show-error="showErrors && invalidFields.birthday"
          :error="t('errorMessages.atLeastOne18')"
        />
        <InputFieldError
          :text="t('errorMessages.updateTraveller')"
          v-if="error"
          class="px-4"
        />
      </div>
    </section>
  </SlidingPanel>
</template>

<script lang="ts" setup>
  // Vue
  import { ComputedRef, computed, defineProps, ref, Ref } from 'vue'

  // Lodash
  import _ from 'lodash'

  // Types
  import {
    BookingSummary,
    TravellerSummary,
  } from '@/services/BookingService.interface'

  // Components
  import SlidingPanel from '@/components/UI/SlidingPanel.vue'
  import Input from '@/components/UI/Input.vue'
  import DatePicker from '@/components/UI/DatePicker.vue'
  import InputFieldError from '@/components/UI/InputFieldError.vue'

  // Store
  import { useStore } from '@/store'
  const store = useStore()

  // Translations
  import { useI18n } from 'vue-i18n'
  const { t } = useI18n()

  // Utils
  import { getAge, validateName } from '@/utils'

  // Props
  interface Props {
    selectedTraveller: TravellerSummary | null
    panelTitle: string
    panelId: string
  }

  const props = defineProps<Props>()

  // Refs
  const updatedTravellerData: Ref<Partial<TravellerSummary>> = ref({
    firstName: props.selectedTraveller?.firstName || '',
    lastName: props.selectedTraveller?.lastName || '',
    birthday: props.selectedTraveller?.birthday || '',
  })

  const submitted: Ref<boolean> = ref(false)

  const isPanelOpen: ComputedRef<boolean> = computed(() => {
    const checkIsOpen = store.getters['modal/isOpen']
    return checkIsOpen(props.panelId)
  })

  // Store values
  const isLoading: ComputedRef<boolean> = computed(() =>
    store.getters['booking/isLoading']('updateTraveller')
  )

  const error: ComputedRef<string> = computed(() =>
    store.getters['booking/getError']('updateTraveller')
  )

  const selectedBooking: ComputedRef<BookingSummary> = computed(() => {
    return store.getters['booking/selectedBooking']
  })

  const travellers: ComputedRef<TravellerSummary[]> = computed(() => {
    return selectedBooking.value.travellers
  })

  // Computed

  const invalidFields: ComputedRef<{
    firstName: boolean
    lastName: boolean
    birthday: boolean
  }> = computed(() => {
    return {
      firstName: !validateName(updatedTravellerData.value.firstName),
      lastName: !validateName(updatedTravellerData.value.lastName),
      birthday: !atLeastOneAdult.value,
    }
  })

  const missingMandatoryInfo: ComputedRef<boolean> = computed(() => {
    return (
      invalidFields.value.firstName ||
      invalidFields.value.lastName ||
      invalidFields.value.birthday
    )
  })

  const showErrors: ComputedRef<boolean> = computed(() => {
    return (
      submitted.value && (missingMandatoryInfo.value || !atLeastOneAdult.value)
    )
  })

  // Get the ages of travellers with the current age selected in the form
  const ages: ComputedRef<string[]> = computed(() => {
    if (!props.selectedTraveller) return []

    const updatedTravellers = travellers.value.map(
      (traveller: TravellerSummary) =>
        traveller.id === props.selectedTraveller?.id
          ? { ...traveller, ...updatedTravellerData.value }
          : traveller
    )

    const departureDate = selectedBooking.value?.departureDate
    return updatedTravellers.map((traveller: TravellerSummary) =>
      getAge(traveller.birthday, departureDate)
    )
  })

  // Check if at least one of the travellers is 18 and over, if not do not allow the user to select under 18
  const atLeastOneAdult: ComputedRef<boolean> = computed(() => {
    return ages.value.includes('18+')
  })

  // Methods
  const onClose = (): void => {
    store.commit('modal/close', props.panelId)
  }

  const onConfirm = async (): Promise<void> => {
    try {
      submitted.value = true
      if (showErrors.value) return
      await store.dispatch('booking/updateTraveller', {
        ...props.selectedTraveller,
        ...updatedTravellerData.value,
      })
      onClose()
    } catch (error) {
      console.error('Failed to update traveller:', error)
    }
  }

  const updateFields = (field: keyof TravellerSummary, value: string): void => {
    console.log(" updateFields ~ value:", value)
    if (field === 'birthday') {
      updatedTravellerData.value.birthday = value
    } else if (field === 'firstName') {
      updatedTravellerData.value.firstName = value
    } else if (field === 'lastName') {
      updatedTravellerData.value.lastName = value
    }
  }
</script>
